import { Fragment, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { observer } from 'decorators';
import { makeStyles, useDisposable } from 'hooks';
import { Typography, Grid, Tooltip, IconButton } from '@material-ui/core';
import ReactCrop from 'react-image-crop';
import Modal from 'components/modal';
import Button from 'components/button';
import Spinner from 'components/spinner';
import { getScrollParent, acceptImageTypes } from 'utils/dom';

import ImageUploadStore from './imageUpload.store';
import Dropzone from './index';

import { AlertCircleOutline, RotateLeft, RotateRight } from 'mdi-material-ui';

import 'react-image-crop/dist/ReactCrop.css';

const noop = () => {};
export default observer(forwardRef(function ImageUploadView({ modalRef, inline, inlinePortalRef, onRefresh: handleRefresh, disabled, ...props }, ref) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleRefresh || noop);

  const store = useDisposable(() => new ImageUploadStore(props));
  store.setProps(props);
  const dropzoneRef = useRef(null);
  const containerRef = useRef(null);

  const { children, cropSettings, aspect, ...other } = props;
  const { image, isLoadingImage } = store;

  const handleImages = useCallback(f => store.handleImages(f), [ store ]);
  const handleCancel = useCallback(() => store.cancel(), [ store ]);

  // Auto-scroll to top section when inline and exit out of image section
  const isInlineAndExpanded = !!(inline && (isLoadingImage || image));
  useEffect(() => {
    // When expanded we will have a dispose that scrolls back to top
    // ie when it changes back to not expanded
    if (!isInlineAndExpanded) { return; }

    const ref = containerRef.current;
    if (!ref) { return; }

    const parent = getScrollParent(ref);
    if (!parent) { return; }

    const scollPos = parent.scrollTop;
    return () => {
      const p = getScrollParent(ref);
      if (p) { p.scrollTop = scollPos; }
    };
  }, [ isInlineAndExpanded ]);

  useImperativeHandle(ref, () => ({
    open: e => {
      dropzoneRef.current && dropzoneRef.current.open();
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    handleImage: f => handleImages([{ file: f }])
  }));

  let inner = children;
  if (!disabled) {
    inner = <Dropzone
      {...other}
      autoUpload
      multiple={false}
      accept={acceptImageTypes}
      onManualFilesHandle={handleImages}
      ref={dropzoneRef}
      containerRef={containerRef}
    >
      { children }
    </Dropzone>;
  }

  return <Fragment>
    { inner }
    { !inline && (isLoadingImage || image) &&
      <Modal label="Crop Image" open disableBackdropClick onClose={handleCancel} ref={modalRef}>
        <ImageUploadEditor store={store} aspect={aspect} cropSettings={cropSettings} onRefresh={handleRefresh} />
      </Modal>
    }
    { isInlineAndExpanded &&
      ReactDOM.createPortal(
        <div ref={modalRef}>
          <ImageUploadEditor inline store={store} aspect={aspect} cropSettings={cropSettings} onRefresh={handleRefresh} />
        </div>, inlinePortalRef.current
      )
    }
  </Fragment>;
}));

const useEditorStyles = makeStyles(theme => ({
  crop: {
    backgroundColor: `${theme.palette.common.white} !important`,
    boxShadow: theme.shadows[1]
  }
}));

const ImageUploadEditor = observer(function ImageUploadEditor({ store, cropSettings, aspect, inline, onRefresh }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onRefresh || noop);
  const classes = useEditorStyles();
  const { image, isLoadingImage, crop, isSaving, canSave, isRotating } = store;

  const handleCropChange = useCallback(c => store.cropChange(c), [ store ]);
  const handleImageLoaded = useCallback(e => store.imageLoaded(e.currentTarget, aspect), [ store, aspect ]);
  const handleFinishCrop = useCallback(() => store.finishCrop(), [ store ]);
  const handleCancel = useCallback(() => store.cancel(), [ store ]);

  const handleRotateLeft = useCallback(() => store.rotateImage(false), [ store ]);
  const handleRotateRight = useCallback(() => store.rotateImage(true), [ store ]);

  return <Grid container direction="column" spacing={2}>
    <Grid item>
      Use the cropping tool to select the portion of the image you wish to keep. To correct the image&apos;s orientation,{' '}
      use the rotate icons to move it 90 degrees to the left or right.
    </Grid>
    <Grid item>
      <Grid container>
        <Grid item>
          <Tooltip title="Rotate Left">
            <span>
              <IconButton disabled={isLoadingImage || isRotating} onClick={handleRotateLeft} aria-label="Rotate Left">
                <RotateLeft />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs />
        <Grid item>
          <Tooltip title="Rotate Right">
            <span>
              <IconButton disabled={isLoadingImage || isRotating} onClick={handleRotateRight} aria-label="Rotate Right">
                <RotateRight />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
    <Grid item>
      { (isLoadingImage || isRotating) &&
        <Spinner text="Loading..." />
      }
      { !isRotating && !isLoadingImage &&
        <ReactCrop
          crop={crop}
          aspect={aspect}
          className={classes.crop}
          onChange={handleCropChange}
          {...cropSettings}
        >
          <img src={image} onLoad={handleImageLoaded} />
        </ReactCrop>
      }
    </Grid>
    { !isRotating && !isLoadingImage && !canSave &&
      <Grid item>
        <Typography color="error" align="right">
          <AlertCircleOutline /> Please select the area of your image you want to be visible
        </Typography>
      </Grid>
    }
    <Grid item>
      <Grid container spacing={2}>
        { !inline && <Grid item xs /> }
        <Grid item><Button onClick={handleCancel}>Cancel</Button></Grid>
        { inline && <Grid item xs /> }
        <Grid item><Button color="primary" disabled={!isLoadingImage && !canSave} onClick={handleFinishCrop} isLoading={isSaving}>Save</Button></Grid>
      </Grid>
    </Grid>
  </Grid>;
});